/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  PropsWithChildren,
  PropsWithoutRef,
  ReactElement,
  RefAttributes,
  useImperativeHandle,
  useRef,
} from "react";

import * as React from "react";
import { ElementType } from "components/shared/WebComponents";

// Necessary for casting styled webcomponents such as sp-button
export type ClickableComponentClass = React.ComponentClass<{
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}>;

export type WebComponentShimProps<T extends keyof JSX.IntrinsicElements> = {
  className?: string;
} & JSX.IntrinsicElements[T];

export const webComponentShim = <T extends keyof JSX.IntrinsicElements>(
  component: T
): React.ForwardRefExoticComponent<
  PropsWithoutRef<WebComponentShimProps<T>> &
    RefAttributes<ElementType<T> | null>
> => {
  return React.forwardRef<ElementType<T> | null, WebComponentShimProps<T>>(
    (
      props: PropsWithChildren<WebComponentShimProps<T>>,
      externalRef
    ): ReactElement => {
      const { children, className, ...webComponentProps } = props;

      const internalRef = useRef<ElementType<T> | null>(null);
      useImperativeHandle<ElementType<T> | null, ElementType<T> | null>(
        externalRef,
        () => internalRef.current
      );

      // TS gets very confused when using JSX here for some reason. The error ir produces is
      // ts2590: Expression produces a union type that is too complex to represent.
      return React.createElement(
        component,
        {
          ...webComponentProps,
          ref: internalRef,
          class: className,
        },
        children
      );
    }
  );
};
